import {useCallback, useEffect, useMemo, useState} from 'react';

import {Button, Modal, Select} from '@/components';
import {getProcessorOptionsFromCodes} from '@/constants/formatters';
import type {
  ResolverCapabilityMatrix,
  ResolverProviderMap,
} from '@/services/processorService';
import type {
  AfriexCurrency,
  AfriexCurrencyCode,
  AfriexTransactionProcessor,
} from '@/types';

import type {ResolverFamily} from './types';
import {RESOLVER_FAMILIES} from './types';

type ResolverValues = Partial<
  Record<ResolverFamily, AfriexTransactionProcessor | ''>
>;

type UpdateResolverModalProps = {
  currency?: AfriexCurrency;
  providers: ResolverProviderMap;
  matrix: ResolverCapabilityMatrix;
  isOpen: boolean;
  onClose: VoidFunction;
  onSubmit: (values: ResolverValues) => Promise<void>;
};

const UpdateResolverModal = ({
  currency,
  providers,
  matrix,
  isOpen,
  onClose,
  onSubmit,
}: UpdateResolverModalProps) => {
  const [values, setValues] = useState<ResolverValues>({});
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Seeded by iterating RESOLVER_FAMILIES rather than naming the families here.
   *
   * Naming them is how `card` came to be missing: it was added to the family
   * list when the card vault moved onto `params.resolvers`, but not to this
   * seed, so its field always rendered "Not set". `handleUpdateResolvers` sends
   * the full desired state and drops empty selections, so every save of any
   * currency silently deleted its configured card vault. Deriving the seed from
   * the same list the fields are rendered from makes that unrepeatable.
   */
  useEffect(() => {
    setValues(
      Object.fromEntries(
        RESOLVER_FAMILIES.map(({key}) => [
          key,
          currency?.params?.resolvers?.[key] ?? '',
        ]),
      ),
    );
  }, [currency]);

  /**
   * One pass per family: the eligible providers for this currency, the options
   * to render, and whether the field can be edited.
   *
   * Options come from the server's registered-adapter lists intersected with
   * the capability matrix for this row's currency, not from the processor enum.
   * Only a subset of processors can resolve a name at all, and only a subset of
   * those can resolve one for a given currency; `patchCurrencySchema` would
   * happily store either kind of wrong value.
   *
   * A stored value neither list offers is appended with a marker rather than
   * dropped, so the field never renders blank and the operator can see what is
   * configured before changing it. Same treatment the Corridors modal gives an
   * unavailable current processor, and the server's write check is built around
   * it: an unchanged value saves, a newly chosen one does not.
   */
  const fields = useMemo(
    () =>
      RESOLVER_FAMILIES.map((family) => {
        const currencyCode = currency?.code as AfriexCurrencyCode;
        const eligible = matrix?.[family.key]?.[currencyCode] ?? [];
        const available = (providers?.[family.key] ?? []).filter((processor) =>
          eligible.includes(processor),
        );
        // Narrowed once: `values[key]` is `AfriexTransactionProcessor | ''`,
        // and '' means "not configured" rather than a processor.
        const stored = values[family.key] || undefined;
        const isUnavailable = Boolean(stored && !available.includes(stored));
        const codes =
          stored && isUnavailable ? [...available, stored] : available;

        // Providers the server has switched off for this currency: shown, but
        // not selectable, so it is clear why they are missing.
        const switchedOffNotes = new Map<string, string>([
          ...(matrix?.dev?.[family.key]?.[currencyCode] ?? []).map(
            (code) => [String(code), 'not live in production'] as const,
          ),
          ...(matrix?.retired?.[family.key]?.[currencyCode] ?? []).map(
            (code) => [String(code), 'retired'] as const,
          ),
        ]);
        const storedCode: string = stored ?? '';
        const switchedOff = [...switchedOffNotes.keys()].filter(
          (code) => code !== storedCode,
        );

        return {
          ...family,
          hasEligible: available.length > 0,
          // Disabled only when there is nothing to choose AND nothing to
          // clear. A stale value must always stay clearable, or a currency
          // whose provider was later restricted could never be corrected.
          isDisabled: available.length === 0 && !stored && !switchedOff.length,
          options: [
            {key: 'Not set', value: ''},
            ...getProcessorOptionsFromCodes(codes).map((option) =>
              isUnavailable && option.value === stored
                ? {
                    ...option,
                    key: `${option.key} (configured, ${
                      switchedOffNotes.get(option.value) ?? 'not available'
                    })`,
                  }
                : option,
            ),
            ...getProcessorOptionsFromCodes(switchedOff).map((option) => ({
              ...option,
              key: `${option.key} (!) ${switchedOffNotes.get(option.value)}`,
              disabled: true,
            })),
          ],
        };
      }),
    [providers, matrix, currency, values],
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setIsSaving(true);
      try {
        await onSubmit(values);
      } finally {
        setIsSaving(false);
      }
    },
    [onSubmit, values],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-5/6 md:w-2/5 lg:w-1/3"
      height="h-auto"
      title={`${currency?.code ?? ''} Resolvers`}
      closeOnOutsideClick
    >
      <form onSubmit={handleSubmit} className="flex flex-col space-y-5 mt-4">
        {fields.map(({key, label, hint, options, isDisabled, hasEligible}) => (
          <div key={key}>
            <Select
              className="w-full"
              options={options}
              value={values[key] ?? ''}
              label={label}
              disabled={isDisabled}
              // The shared Select renders its own placeholder as `disabled`,
              // so the explicit "Not set" option above is what makes clearing
              // a resolver possible at all.
              placeholder="Not set"
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                setValues((previous) => ({
                  ...previous,
                  [key]: event.target.value as AfriexTransactionProcessor,
                }))
              }
            />
            {/*
              Naming the currency and the family matters: an empty dropdown on
              its own reads as a screen that failed to load, which is exactly
              what a failed providers fetch also looks like.
            */}
            <p
              className={`mt-1 text-xs ${
                hasEligible
                  ? 'text-slate-500 dark:text-slate-400'
                  : 'text-amber-600 dark:text-amber-500'
              }`}
              data-testid={`resolver-hint-${key}`}
            >
              {hasEligible
                ? hint
                : `No registered provider resolves ${label.toLowerCase()} for ${
                    currency?.code ?? 'this currency'
                  }.`}
            </p>
          </div>
        ))}

        <Button type="submit" variant="solid" disabled={isSaving}>
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </form>
    </Modal>
  );
};

export default UpdateResolverModal;
